import { useState } from 'react';
import pluralize from 'pluralize';
import { MIN_THRESHOLD, MAX_THRESHOLD } from '../api/match';
import ConfirmDialog from '../components/ConfirmDialog';
import DeleteAccountDialog from '../components/DeleteAccountDialog';
import { useSpotifyStatus } from '../hooks/useSpotifyStatus';
import { useMe } from '../hooks/useMe';
import { useConnectSpotify } from '../hooks/useConnectSpotify';
import { useDisconnectSpotify } from '../hooks/useDisconnectSpotify';
import { useUpdateMatchThreshold } from '../hooks/useUpdateMatchThreshold';
import { useCreateIcalToken } from '../hooks/useCreateIcalToken';
import { useRevokeIcalToken } from '../hooks/useRevokeIcalToken';
import { useResetNotInterested } from '../hooks/useResetNotInterested';
import { useUpdateShowSetlists } from '../hooks/useUpdateShowSetlists';
import * as s from './SettingsPage.css';
import * as c from '../styles/common.css';
import Layout from '../components/Layout';
import { useListNotInterested } from '../hooks/useListNotInterested';

export default function SettingsPage() {
  const { data: spotifyStatus, isLoading: spotifyStatusLoading } = useSpotifyStatus();
  const connectSpotifyMut = useConnectSpotify();
  const disconnectSpotifyMut = useDisconnectSpotify();
  const hiddenList = useListNotInterested();

  const { data: me } = useMe();
  const loadedPercent = Math.round((me?.score_threshold ?? 0.3) * 100);
  const [percent, setPercent] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const effectivePercent = percent ?? loadedPercent;

  const saveThreshold = useUpdateMatchThreshold();

  const minPercent = Math.round(MIN_THRESHOLD * 100);
  const maxPercent = Math.round(MAX_THRESHOLD * 100);
  const dirty = percent !== null && percent !== loadedPercent;

  const [icalURL, setIcalURL] = useState<string | null>(null);
  const generateIcal = useCreateIcalToken();
  const revokeIcal = useRevokeIcalToken();

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const resetNotInterestedMut = useResetNotInterested();

  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const showSetlists = me?.show_setlists ?? false;
  const saveShowSetlists = useUpdateShowSetlists();

  return (
    <Layout>
      <div>
        <div className={c.pageHeader}>
          <h1 className={c.pageTitle}>Settings</h1>
        </div>
        <div>
          {/* Match sensitivity */}
          <section className={c.section}>
            <h2 className={c.sectionTitle}>Match sensitivity</h2>
            <p className={s.desc}>
              Lower = more events; higher = stricter, fewer but more relevant events.
            </p>
            <div className={s.sliderRow}>
              <input
                type="range"
                aria-label="Match sensitivity"
                min={minPercent}
                max={maxPercent}
                step={1}
                value={effectivePercent}
                onChange={(e) => {
                  setPercent(Number(e.target.value));
                  setSaveError(false);
                }}
                className={s.slider}
              />
              <span className={s.percent}>{effectivePercent}%</span>
            </div>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!dirty || saveThreshold.isPending}
              className={s.saveButton}
            >
              Save threshold
            </button>
            {saveError && (
              <p role="alert" className={s.error}>
                Could not update your threshold. Please try again.
              </p>
            )}
          </section>

          {/* Setlists */}
          <section className={c.section}>
            <h2 className={c.sectionTitle}>Setlists</h2>
            <p className={s.desc}>
              Setlists spoil the show for some people, so they stay hidden until you ask for them.
              Turn this on to see the songs an artist has been playing on tour.
            </p>
            <div className={s.toggleRow}>
              <label className={s.toggleLabel}>
                <input
                  type="checkbox"
                  checked={showSetlists}
                  disabled={saveShowSetlists.isPending}
                  onChange={(e) => saveShowSetlists.mutate(e.target.checked)}
                />
                Show setlists
              </label>
            </div>
            {saveShowSetlists.isError && (
              <p role="alert" className={s.error}>
                Could not update your setlist preference. Please try again.
              </p>
            )}
          </section>

          {/* Spotify */}
          <section className={c.section}>
            <h2 className={c.sectionTitle}>Spotify</h2>
            <p className={s.desc}>
              Connect Spotify to get matches based on your top artists and genres.
            </p>
            {!spotifyStatusLoading && (
              <div className={s.row}>
                {spotifyStatus?.connected ? (
                  <>
                    <span className={s.connectedText}>Connected.</span>
                    <button
                      type="button"
                      onClick={() => disconnectSpotifyMut.mutate()}
                      disabled={disconnectSpotifyMut.isPending}
                      className={c.buttonSecondary}
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => connectSpotifyMut.mutate()}
                    disabled={connectSpotifyMut.isPending}
                    className={s.connectButton}
                  >
                    Connect Spotify
                  </button>
                )}
              </div>
            )}
          </section>

          {/* iCal */}
          <section className={c.section}>
            <h2 className={c.sectionTitle}>Calendar subscription</h2>
            <p className={s.desc}>
              Generate a URL you can paste into iOS Calendar, Google Calendar, or Fantastical to
              subscribe to your matched events.
            </p>
            <div className={s.buttonRow}>
              <button
                type="button"
                onClick={() =>
                  generateIcal.mutate(undefined, { onSuccess: (data) => setIcalURL(data.url) })
                }
                className={c.buttonPrimary}
              >
                Generate calendar URL
              </button>
              <button
                type="button"
                onClick={() => revokeIcal.mutate(undefined, { onSuccess: () => setIcalURL(null) })}
                className={c.buttonSecondary}
              >
                Revoke
              </button>
            </div>
            {icalURL && <code className={s.codeBlock}>{icalURL}</code>}
          </section>

          {/* Hidden events */}
          <section className={c.section}>
            <h2 className={c.sectionTitle}>Hidden events</h2>
            <p className={s.desc}>
              Reset events that you've hidden via the "Hide" button.{' '}
              {hiddenList.data?.length
                ? `You've hidden ${hiddenList.data.length} ${pluralize('event', hiddenList.data.length)}.`
                : ''}
            </p>
            <button
              type="button"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetNotInterestedMut.isPending}
              className={s.resetButton}
            >
              Reset hidden events
            </button>
          </section>

          {/* Delete account */}
          <section className={c.section}>
            <h2 className={c.sectionTitle}>Delete account</h2>
            <p className={s.desc}>
              Permanently delete your account and all of your data. This can&rsquo;t be undone.
            </p>
            <button
              type="button"
              onClick={() => setDeleteAccountOpen(true)}
              className={s.deleteAccountButton}
            >
              Delete account
            </button>
          </section>

          <ConfirmDialog
            open={confirmOpen}
            title="Update match threshold?"
            message="Updating your match threshold will recalculate all of your recommended events. Continue?"
            onConfirm={() =>
              saveThreshold.mutate(effectivePercent / 100, {
                onSuccess: () => {
                  setConfirmOpen(false);
                  setPercent(null);
                  setSaveError(false);
                },
                onError: () => {
                  setConfirmOpen(false);
                  setSaveError(true);
                },
              })
            }
            onCancel={() => setConfirmOpen(false)}
          />
          <ConfirmDialog
            open={resetConfirmOpen}
            title="Reset hidden events?"
            message={`This clears every event you've hidden via the "Hide" button. They will reappear in your calendar if they're still upcoming. Continue?`}
            onConfirm={() =>
              resetNotInterestedMut.mutate(undefined, {
                onSuccess: () => setResetConfirmOpen(false),
              })
            }
            onCancel={() => setResetConfirmOpen(false)}
          />
          <DeleteAccountDialog
            open={deleteAccountOpen}
            onClose={() => setDeleteAccountOpen(false)}
          />
        </div>
      </div>
    </Layout>
  );
}
